'use client';

/**
 * TCO Service Referrals List Component
 *
 * Displays a list of all service referrals for care organizations (TCO context) with:
 * - Filtering by status, urgency, source type, and SDOH domain
 * - Search functionality for members
 * - Quick actions for common operations
 * - Navigation to detailed workspace view
 *
 * Note: viewing the list needs any referral-producing capability (TRUS-749):
 * tco_platform, care_referrals, care_plan_management or employer_platform —
 * the same set the backend's read routes accept. An org that can send a
 * referral must be able to see it.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Filter,
  Plus,
  RefreshCw,
  FileText,
  Clock,
  User,
  Building2,
  Calendar,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  ArrowUpDown,
  Lock,
  Heart,
  ClipboardList,
  Home,
  Utensils,
  Car,
  Briefcase,
  Shield,
  UserCheck,
  Bot,
  Activity,
} from 'lucide-react';
import {
  tcoServiceReferralsAPI,
  TCOServiceReferral,
  ReferralStatus,
  TCOSourceType,
  UrgencyLevel,
  SDOHDomainCode,
  getReferralStatusLabel,
  getReferralStatusColor,
  getUrgencyLabel,
  getSourceTypeLabel,
} from '@/services/api/tcoServiceReferrals';
import { useHasAnyCapability } from '@/hooks/useCapabilities';

// Mirrors REFERRAL_READ_CAPABILITIES in the backend's tco_service_referrals.py.
const REFERRAL_READ_CAPABILITIES = ['tco_platform', 'care_referrals', 'care_plan_management', 'employer_platform'];
import { useToast } from '@/components/ui/use-toast';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface TCOServiceReferralsListProps {
  onSelectReferral?: (referralId: number) => void;
  onCreateNew?: () => void;
  className?: string;
  /** Optional member ID to filter referrals by member */
  memberId?: number;
  /** Optional care plan ID to filter referrals by care plan */
  carePlanId?: number;
  /** Organization ID for capability checking - defaults to active_cbo_id from localStorage */
  organizationId?: number;
}

// Status filter options
const STATUS_FILTERS: { value: ReferralStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'created', label: 'Created' },
  { value: 'pending_consent', label: 'Pending Consent' },
  { value: 'matching', label: 'Matching' },
  { value: 'matched', label: 'Matched' },
  { value: 'provider_notified', label: 'Provider Notified' },
  { value: 'provider_accepted', label: 'Provider Accepted' },
  { value: 'service_scheduled', label: 'Service Scheduled' },
  { value: 'service_started', label: 'Service Started' },
  { value: 'service_completed', label: 'Service Completed' },
  { value: 'closed', label: 'Closed' },
];

// Source type filter options
const SOURCE_TYPE_FILTERS: { value: TCOSourceType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Sources' },
  { value: 'care_plan_goal', label: 'Care Plan Goal' },
  { value: 'sdoh_screening', label: 'SDOH Screening' },
  { value: 'navigator_referral', label: 'Navigator Referral' },
  { value: 'member_request', label: 'Member Request' },
  { value: 'pcp_referral', label: 'PCP Referral' },
  { value: 'discharge_planning', label: 'Discharge Planning' },
  { value: 'case_review', label: 'Case Review' },
  { value: 'ai_recommended', label: 'AI Recommended' },
];

// SDOH Domain icons - mapped to SDOHDomainCode enum values
const SDOH_ICONS: Record<SDOHDomainCode, React.ReactNode> = {
  HOUSING: <Home className="w-4 h-4" />,
  FOOD_NUTRITION: <Utensils className="w-4 h-4" />,
  TRANSPORTATION: <Car className="w-4 h-4" />,
  ECONOMIC_STABILITY: <Building2 className="w-4 h-4" />,
  EDUCATION_EMPLOYMENT: <Briefcase className="w-4 h-4" />,
  SOCIAL_SUPPORT: <Heart className="w-4 h-4" />,
  HEALTH_BEHAVIORS: <Shield className="w-4 h-4" />,
  SAFETY: <AlertCircle className="w-4 h-4" />,
};

// ============================================================================
// COMPONENT
// ============================================================================

export function TCOServiceReferralsList({
  onSelectReferral,
  onCreateNew,
  className = '',
  memberId,
  carePlanId,
  organizationId,
}: TCOServiceReferralsListProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  // Get organization ID from props or localStorage
  const effectiveOrgId = organizationId || parseInt(localStorage.getItem('active_cbo_id') || '0', 10);

  // Any referral-producing capability can view the org's referrals
  const { hasAny: hasTCOPlatform, loading: capabilityLoading } = useHasAnyCapability(
    effectiveOrgId,
    REFERRAL_READ_CAPABILITIES
  );

  const { toast } = useToast();

  // State
  const [referrals, setReferrals] = useState<TCOServiceReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | 'all'>('all');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<TCOSourceType | 'all'>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'updated_at' | 'urgency'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Load referrals
  const loadReferrals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await tcoServiceReferralsAPI.list({
        member_id: memberId,
        care_plan_id: carePlanId,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        source_type: sourceTypeFilter !== 'all' ? sourceTypeFilter : undefined,
        search: searchQuery || undefined,
      });

      // Sort locally
      const sorted = [...data].sort((a, b) => {
        let aVal: any, bVal: any;
        if (sortBy === 'urgency') {
          const urgencyOrder = { emergent: 0, urgent: 1, routine: 2 };
          aVal = urgencyOrder[a.urgency_level as keyof typeof urgencyOrder] ?? 2;
          bVal = urgencyOrder[b.urgency_level as keyof typeof urgencyOrder] ?? 2;
        } else {
          aVal = new Date(a[sortBy] || '').getTime();
          bVal = new Date(b[sortBy] || '').getTime();
        }
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      });

      setReferrals(sorted);
    } catch (err) {
      console.error('Failed to load TCO service referrals:', err);
      setError('Failed to load service referrals. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [memberId, carePlanId, statusFilter, sourceTypeFilter, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    if (hasTCOPlatform) {
      loadReferrals();
    }
  }, [loadReferrals, hasTCOPlatform]);

  // Format date for display
  const formatDate = (dateStr: string) => {
    return fmt.medium(dateStr);
  };

  // Toggle sort order
  const toggleSort = (field: 'created_at' | 'updated_at' | 'urgency') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Get status icon
  const getStatusIcon = (status: ReferralStatus) => {
    if (status.includes('completed') || status === 'closed') {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    if (status.includes('declined') || status.includes('cancelled')) {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    if (status.includes('pending') || status.includes('scheduled') || status === 'matching') {
      return <Clock className="w-4 h-4 text-yellow-500" />;
    }
    return <AlertCircle className="w-4 h-4 text-blue-500" />;
  };

  // Get source type icon
  const getSourceTypeIcon = (sourceType?: TCOSourceType) => {
    switch (sourceType) {
      case 'care_plan_goal':
        return <ClipboardList className="w-4 h-4 text-indigo-500" />;
      case 'sdoh_screening':
        return <Heart className="w-4 h-4 text-pink-500" />;
      case 'navigator_referral':
        return <UserCheck className="w-4 h-4 text-blue-500" />;
      case 'member_request':
        return <User className="w-4 h-4 text-green-500" />;
      case 'pcp_referral':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'ai_recommended':
        return <Bot className="w-4 h-4 text-amber-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get urgency color
  const getUrgencyColor = (urgency?: UrgencyLevel) => {
    switch (urgency) {
      case 'emergent':
        return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
      case 'urgent':
        return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
    }
  };

  // Calculate stats
  const stats = {
    total: referrals.length,
    inProgress: referrals.filter(r =>
      ['service_started', 'service_scheduled', 'provider_accepted'].includes(r.status)
    ).length,
    pending: referrals.filter(r =>
      ['created', 'pending_consent', 'matching', 'matched', 'provider_notified'].includes(r.status)
    ).length,
    completed: referrals.filter(r =>
      ['service_completed', 'closed'].includes(r.status)
    ).length,
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  // Show loading state while checking capability
  if (capabilityLoading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">{t('tcoReferrals.list.checkingAccess', 'Checking access...')}</span>
      </div>
    );
  }

  // Show access denied if tco_platform capability is not enabled
  if (!hasTCOPlatform) {
    return (
      <div className={`${className}`}>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="p-4 bg-amber-100 rounded-full mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('tcoReferrals.list.tcoPlatformRequired', 'TCO Platform Required')}
            </h2>
            <p className="text-gray-600 max-w-md mb-4">
              {t('tcoReferrals.list.tcoPlatformRequiredDesc', "Care coordination service referrals require the 'tco_platform' capability to be enabled for your organization.")}
            </p>
            <p className="text-sm text-gray-500">
              {t('tcoReferrals.list.contactAdmin', 'If you believe this is an error, please contact your administrator.')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('tcoReferrals.list.title', 'Care Coordination Referrals')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {t('tcoReferrals.list.subtitle', 'Manage service referrals for member care coordination')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadReferrals} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('tcoReferrals.list.refresh', 'Refresh')}
          </Button>
          <Button size="sm" onClick={onCreateNew}>
            <Plus className="w-4 h-4 mr-1" />
            {t('tcoReferrals.list.newReferral', 'New Referral')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
            <div className="text-sm text-gray-500">{t('tcoReferrals.list.stats.totalReferrals', 'Total Referrals')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-500">{t('tcoReferrals.list.stats.pending', 'Pending')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
            <div className="text-sm text-gray-500">{t('tcoReferrals.list.stats.inProgress', 'In Progress')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-gray-500">{t('tcoReferrals.list.stats.completed', 'Completed')}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t('tcoReferrals.list.searchPlaceholder', 'Search by member name, service type...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ReferralStatus | 'all')}
              className="px-3 py-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Source Type Filter */}
            <select
              value={sourceTypeFilter}
              onChange={(e) => setSourceTypeFilter(e.target.value as TCOSourceType | 'all')}
              className="px-3 py-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {SOURCE_TYPE_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleSort('updated_at')}
                className={sortBy === 'updated_at' ? 'border-blue-500' : ''}
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                {t('tcoReferrals.list.updated', 'Updated')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleSort('urgency')}
                className={sortBy === 'urgency' ? 'border-blue-500' : ''}
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                {t('tcoReferrals.list.urgency', 'Urgency')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadReferrals} className="ml-auto">
                {t('tcoReferrals.list.retry', 'Retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && referrals.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Heart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('tcoReferrals.list.noReferralsFound', 'No Care Referrals Found')}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all' || sourceTypeFilter !== 'all'
                ? t('tcoReferrals.list.adjustFilters', 'Try adjusting your search or filter criteria.')
                : t('tcoReferrals.list.getStarted', 'Get started by creating your first care coordination referral.')}
            </p>
            <Button onClick={onCreateNew}>
              <Plus className="w-4 h-4 mr-1" />
              {t('tcoReferrals.list.createNewReferral', 'Create New Referral')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Referrals List */}
      {!loading && !error && referrals.length > 0 && (
        <div className="space-y-3">
          {referrals.map((referral) => {
            // getReferralStatusColor returns a string like 'bg-gray-100 text-gray-800'
            const statusColorClass = getReferralStatusColor(referral.status);
            const urgencyColor = getUrgencyColor(referral.urgency_level);

            return (
              <div
                key={referral.id}
                className="block cursor-pointer"
                onClick={() => {
                  console.log(`[TCOServiceReferralsList] Selecting referral ${referral.id}`);
                  onSelectReferral?.(referral.id);
                }}
              >
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Header Row */}
                        <div className="flex items-center gap-3 mb-2">
                          {getStatusIcon(referral.status)}
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {referral.member_name || `Member #${referral.member_id}`}
                          </span>
                          <Badge
                            variant="outline"
                            className={statusColorClass}
                          >
                            {getReferralStatusLabel(referral.status)}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`${urgencyColor.bg} ${urgencyColor.text} ${urgencyColor.border}`}
                          >
                            {getUrgencyLabel(referral.urgency_level)}
                          </Badge>
                        </div>

                        {/* Details Row */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          {referral.service_type_name && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              {referral.service_type_name}
                            </span>
                          )}
                          {referral.source_type && (
                            <span className="flex items-center gap-1">
                              {getSourceTypeIcon(referral.source_type)}
                              {getSourceTypeLabel(referral.source_type)}
                            </span>
                          )}
                          {referral.referring_navigator_name && (
                            <span className="flex items-center gap-1">
                              <UserCheck className="w-4 h-4" />
                              {referral.referring_navigator_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(referral.created_at)}
                          </span>
                        </div>

                        {/* SDOH Domain + clinical-trigger provenance tags */}
                        {(referral.sdoh_domain_code || referral.origin_clinical_trigger) && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {referral.sdoh_domain_code && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200"
                              >
                                {SDOH_ICONS[referral.sdoh_domain_code]}
                                <span className="ml-1">{referral.sdoh_domain_code.replace(/_/g, ' ')}</span>
                              </Badge>
                            )}
                            {referral.origin_clinical_trigger && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-teal-50 text-teal-700 border-teal-200"
                                title="Auto-created from a clinical trigger"
                              >
                                <Activity className="w-3 h-3" />
                                <span className="ml-1 capitalize">
                                  {referral.origin_clinical_trigger.replace(/_/g, ' ')}
                                  {referral.origin_icd10 ? ` · ${referral.origin_icd10}` : ''}
                                </span>
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Care Plan Link */}
                        {referral.care_plan_id && (
                          <div className="mt-2 text-sm">
                            <span className="text-gray-500">{t('tcoReferrals.list.carePlan', 'Care Plan')}: </span>
                            <span className="font-medium text-indigo-600">
                              #{referral.care_plan_id}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Arrow */}
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TCOServiceReferralsList;
